import React from 'react';
import { Download, ExternalLink, ArrowRight } from 'lucide-react';
import { Button } from './Button';

interface ComparisonViewProps {
  originalUrl: string;
  processedUrl: string;
  onDownload: () => void;
  onReset: () => void;
}

export const ComparisonView: React.FC<ComparisonViewProps> = ({ 
  originalUrl, 
  processedUrl, 
  onDownload,
  onReset
}) => {
  return (
    <div className="w-full space-y-6 animate-fade-in">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Original */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Original</h3>
          </div>
          <div className="relative group rounded-xl overflow-hidden border border-slate-700 bg-slate-900 aspect-square md:aspect-auto md:h-[500px] flex items-center justify-center">
            <img 
              src={originalUrl} 
              alt="Original" 
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>

        {/* Processed */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-brand-400 uppercase tracking-wider">Edited Result</h3>
            <span className="text-xs text-brand-500 bg-brand-500/10 px-2 py-0.5 rounded-full border border-brand-500/20">
              Generated by Gemini 2.5
            </span>
          </div>
          <div className="relative group rounded-xl overflow-hidden border-2 border-brand-500/50 bg-slate-900 shadow-2xl shadow-brand-500/10 aspect-square md:aspect-auto md:h-[500px] flex items-center justify-center">
             <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/dark-matter.png')] opacity-10 pointer-events-none"></div>
            <img 
              src={processedUrl} 
              alt="Processed" 
              className="max-w-full max-h-full object-contain z-10"
            />
            {/* Overlay Actions */}
            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-4 z-20 backdrop-blur-sm">
              <Button onClick={() => window.open(processedUrl, '_blank')} variant="secondary" icon={<ExternalLink size={18}/>}>
                View Full
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-center bg-slate-800/50 p-6 rounded-xl border border-slate-700">
        <div className="text-slate-400 text-sm">
          Satisfied with the result?
        </div>
        <div className="flex gap-3 w-full sm:w-auto">
          <Button onClick={onReset} variant="ghost" className="flex-1 sm:flex-none">
            Edit Another
          </Button>
          <Button onClick={onDownload} variant="primary" icon={<Download size={18}/>} className="flex-1 sm:flex-none">
            Download Result
          </Button>
        </div>
      </div>
    </div>
  );
};